import React, { useRef, useEffect, useState, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Clock, Users, ArrowRight, ChevronLeft, ChevronRight, MapPin, Calendar as CalendarIcon, Loader2, IndianRupee } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const PopularCourses = () => {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const navigate = useNavigate();

  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start' },
    [Autoplay({ delay: 4000, stopOnInteraction: false })]
  );

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let retryCount = 0;
    const maxRetries = 2;

    const loadTrainings = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/public/training-calendar/featured`, {
          timeout: 10000, // Increased to 10 seconds for production
          headers: {
            'Cache-Control': 'no-cache',
          },
        });
        if (isMounted) {
          setCourses(response.data || []);
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching featured trainings:', err);

        // Retry logic for production network issues
        if (isMounted && retryCount < maxRetries) {
          retryCount++;
          console.log(`Retrying featured trainings fetch (${retryCount}/${maxRetries})...`);
          setTimeout(() => loadTrainings(), 1000 * retryCount); // Exponential backoff
        } else {
          if (isMounted) {
            setCourses([]);
            setLoading(false);
          }
        }
      }
    };

    loadTrainings();

    return () => {
      isMounted = false;
    };
  }, []);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
  }, [emblaApi, onSelect]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  // Don't render section if no courses and not loading
  if (!loading && courses.length === 0) {
    return null;
  }

  return (
    <section ref={ref} className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 space-y-4"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            Our <span className="text-blue-600">Popular Courses</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Most sought-after training programs designed to accelerate your professional growth
          </p>
        </motion.div>

        {/* Loading State */}
        {loading && (
          <div className="flex justify-center items-center py-20">
            <Loader2 className="h-12 w-12 animate-spin text-blue-600" />
          </div>
        )}

        {/* Carousel */}
        {!loading && courses.length > 0 && (
          <div className="relative">
            {/* Navigation Buttons */}
            <div className="absolute -left-4 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-full bg-white shadow-lg hover:bg-blue-50 hover:border-blue-600 disabled:opacity-30"
                onClick={scrollPrev}
                disabled={!canScrollPrev}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
            </div>
            <div className="absolute -right-4 top-1/2 -translate-y-1/2 z-10 hidden lg:block">
              <Button
                variant="outline"
                size="icon"
                className="h-12 w-12 rounded-full bg-white shadow-lg hover:bg-blue-50 hover:border-blue-600 disabled:opacity-30"
                onClick={scrollNext}
                disabled={!canScrollNext}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </div>

            {/* Carousel Container */}
            <div className="overflow-hidden" ref={emblaRef}>
              <motion.div
                className="flex gap-6"
                variants={containerVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
              >
                {courses.map((course, index) => (
                  <motion.div
                    key={course.id}
                    className="flex-[0_0_100%] min-w-0 md:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)]"
                    variants={cardVariants}
                  >
                    <Card
                      className="group h-full hover:shadow-2xl transition-all duration-300 border-0 bg-gradient-to-br from-white to-blue-50/30 cursor-pointer overflow-hidden relative"
                      onClick={() => navigate(`/training-event/${course.slug}`)}
                    >
                      {/* Decorative gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />

                      {/* Poster Image (if available) */}
                      {course.poster_url ? (
                        <div className="relative h-52 overflow-hidden">
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent z-10" />
                          <img
                            src={course.poster_url.startsWith('http') ? course.poster_url : `${BACKEND_URL}${course.poster_url}`}
                            alt={course.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                            onError={(e) => {
                              e.target.style.display = 'none';
                              e.target.parentElement.style.display = 'none';
                            }}
                          />
                          {/* Badges on image */}
                          <div className="absolute top-3 right-3 z-20 flex gap-2">
                            {course.is_featured && (
                              <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white border-0 shadow-lg">
                                ⭐ Featured
                              </Badge>
                            )}
                            <Badge className={`${
                              course.seats_available > 0
                                ? 'bg-gradient-to-r from-green-500 to-emerald-600'
                                : 'bg-gradient-to-r from-red-500 to-rose-600'
                            } text-white border-0 shadow-lg`}>
                              {course.seats_available > 0
                                ? `${course.seats_available} seats`
                                : 'Full'}
                            </Badge>
                          </div>
                          {/* Mode badge at bottom left */}
                          <div className="absolute bottom-3 left-3 z-20">
                            <Badge className="bg-white/90 backdrop-blur-sm text-blue-700 border-0 shadow-md">
                              {course.mode || 'Training'}
                            </Badge>
                          </div>
                        </div>
                      ) : (
                        // No poster - show gradient header
                        <div className="relative h-24 bg-gradient-to-r from-blue-600 to-indigo-600">
                          <div className="absolute inset-0 opacity-10">
                            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptLTEyIDEyYzMuMzE0IDAgNiAyLjY4NiA2IDZzLTIuNjg2IDYtNiA2LTYtMi42ODYtNi02IDIuNjg2LTYgNi02eiIgZmlsbD0iI2ZmZiIvPjwvZz48L3N2Zz4=')] opacity-20" />
                          </div>
                          {/* Badges for no-poster version */}
                          <div className="absolute top-3 right-3 flex gap-2">
                            {course.is_featured && (
                              <Badge className="bg-yellow-400 text-gray-900 border-0 shadow-lg">
                                ⭐ Featured
                              </Badge>
                            )}
                            <Badge className={`${
                              course.seats_available > 0
                                ? 'bg-green-500'
                                : 'bg-red-500'
                            } text-white border-0 shadow-lg`}>
                              {course.seats_available > 0
                                ? `${course.seats_available} seats`
                                : 'Full'}
                            </Badge>
                          </div>
                          <div className="absolute bottom-3 left-3">
                            <Badge className="bg-white text-blue-700 border-0">
                              {course.mode || 'Training'}
                            </Badge>
                          </div>
                        </div>
                      )}

                      <CardHeader className="pb-3 relative z-10">
                        <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-blue-600 transition-colors leading-tight line-clamp-2 mb-3">
                          {course.banner_heading || course.title}
                        </CardTitle>
                      </CardHeader>

                      <CardContent className="space-y-4 relative z-10">
                        {/* Training details with enhanced styling */}
                        <div className="space-y-3">
                          <div className="flex items-center gap-3 text-gray-700 bg-blue-50/50 p-2 rounded-lg group-hover:bg-blue-100/50 transition-colors">
                            <div className="bg-blue-100 p-2 rounded-lg group-hover:bg-blue-200 transition-colors">
                              <CalendarIcon className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-xs text-gray-500 font-medium">Start Date</p>
                              <p className="text-sm font-semibold">{formatDate(course.date)}</p>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div className="flex items-center gap-2 text-gray-700 bg-purple-50/50 p-2 rounded-lg">
                              <div className="bg-purple-100 p-1.5 rounded">
                                <Clock className="h-3.5 w-3.5 text-purple-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Duration</p>
                                <p className="text-xs font-semibold">{course.duration}</p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 text-gray-700 bg-orange-50/50 p-2 rounded-lg">
                              <div className="bg-orange-100 p-1.5 rounded">
                                <MapPin className="h-3.5 w-3.5 text-orange-600" />
                              </div>
                              <div>
                                <p className="text-xs text-gray-500">Location</p>
                                <p className="text-xs font-semibold truncate">{course.location}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Price and CTA */}
                        <div className="pt-4 border-t-2 border-gray-100 flex items-center justify-between">
                          {course.is_paid ? (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Training Fee</p>
                              <div className="flex items-baseline gap-1">
                                <IndianRupee className="h-5 w-5 text-blue-600" />
                                <span className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                  {course.price?.replace('₹', '')}
                                </span>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="text-xs text-gray-500 mb-1">Training Fee</p>
                              <span className="text-lg font-bold bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">
                                Complimentary
                              </span>
                            </div>
                          )}
                          <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <Button
                              size="sm"
                              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg shadow-blue-600/30 border-0"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/training-event/${course.slug}`);
                              }}
                              disabled={course.seats_available <= 0}
                            >
                              {course.seats_available > 0 ? (
                                <>
                                  Register <ArrowRight className="ml-1 h-3.5 w-3.5" />
                                </>
                              ) : (
                                'Full'
                              )}
                            </Button>
                          </motion.div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Mobile Navigation Dots */}
            {courses.length > 1 && (
              <div className="flex justify-center gap-2 mt-6 lg:hidden">
                {courses.slice(0, 5).map((_, index) => (
                  <button
                    key={index}
                    onClick={() => emblaApi?.scrollTo(index)}
                    className="h-2 w-2 rounded-full bg-gray-300 hover:bg-blue-600 transition-colors"
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* View All Button */}
        {!loading && courses.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <Button
              size="lg"
              variant="outline"
              className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-8 py-6 text-lg transition-all hover:scale-105"
              onClick={() => navigate('/training-programs')}
            >
              View All Courses
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        )}
      </div>
    </section>
  );
};
